import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Download, CheckCircle, Package, Truck, Clock, 
  CreditCard, Sparkles, QrCode, ShieldCheck, AlertTriangle, 
  Layers, X, ExternalLink 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import ClickToPreviewImage from '@/components/ui/ClickToPreviewImage';
import { formatTanggalIndo } from '@/lib/timeUtils';
import { useClientSession } from '@/lib/ClientSessionContext';
import SuratJalanPrint from '@/components/production/SuratJalanPrint';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

// Terbilang function in Indonesian
const terbilang = (n) => {
  const satuan = ['', 'Satu', 'Dua', 'Tiga', 'Empat', 'Lima', 'Enam', 'Tujuh', 'Delapan', 'Sembilan',
    'Sepuluh', 'Sebelas', 'Dua Belas', 'Tiga Belas', 'Empat Belas', 'Lima Belas', 'Enam Belas',
    'Tujuh Belas', 'Delapan Belas', 'Sembilan Belas'];
  const puluhan = ['', '', 'Dua Puluh', 'Tiga Puluh', 'Empat Puluh', 'Lima Puluh',
    'Enam Puluh', 'Tujuh Puluh', 'Delapan Puluh', 'Sembilan Puluh'];

  const convert = (num) => {
    if (num === 0) return '';
    if (num < 20) return satuan[num];
    if (num < 100) return puluhan[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + satuan[num % 10] : '');
    if (num < 200) return 'Seratus ' + convert(num - 100);
    if (num < 1000) return satuan[Math.floor(num / 100)] + ' Ratus ' + convert(num % 100);
    if (num < 2000) return 'Seribu ' + convert(num - 1000);
    if (num < 1000000) return convert(Math.floor(num / 1000)) + ' Ribu ' + convert(num % 1000);
    if (num < 1000000000) return convert(Math.floor(num / 1000000)) + ' Juta ' + convert(num % 1000000);
    return convert(Math.floor(num / 1000000000)) + ' Miliar ' + convert(num % 1000000000);
  };

  const result = convert(Math.round(n)).trim().replace(/\s+/g, ' ');
  return result + ' Rupiah';
};

export default function ClientPODetail({ onBack }) {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [showSuratJalanModal, setShowSuratJalanModal] = useState(false);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const { clientPOs, trashPOs, isLoading: loadingPOs, updatePO } = useClientPOs();
  const { accountsMap } = useBankAccounts();
  const { clientUser } = useClientSession();

  const { data: companySettingsList } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });
  const companySettings = companySettingsList?.[0] || {};

  const { data: databaseBahanList = [] } = useQuery({
    queryKey: ['database-bahan'],
    queryFn: () => base44.entities.DatabaseBahan.list('nama_brand'),
  });

  const { data: prodOrders = [] } = useQuery({
    queryKey: ['production-orders-detail'],
    queryFn: () => base44.entities.ProductionOrder.list(),
  });

  const { data: prodTasks = [] } = useQuery({
    queryKey: ['production-tasks-detail'],
    queryFn: () => base44.entities.ProductionTask.list(),
  });

  // Load order dari Supabase
  useEffect(() => {
    if (loadingPOs) return;
    if (!clientUser) return;

    const found = clientPOs.find(o => o.id === id);
    const foundTrash = trashPOs.find(o => o.id === id);
    const targetPO = found || foundTrash;
    
    if (targetPO) {
      const isDeveloper = 
        clientUser?.role === 'developer' || 
        clientUser?.role === 'admin' || 
        clientUser?.brand_name?.toLowerCase().includes('dev') || 
        clientUser?.email?.toLowerCase().includes('satalawebdeveloper') ||
        clientUser?.email?.toLowerCase().includes('dev');

      const assignedBrands = isDeveloper ? [] : (clientUser?.assigned_brands || (clientUser?.brand_name ? [clientUser.brand_name] : []));
      const poBrand = (targetPO.brand_name || targetPO.customerName || '').toLowerCase();
      const brandMatches = assignedBrands.some(b => poBrand.includes(b.toLowerCase()));

      const isOwner = isDeveloper || 
                      targetPO.clientId === clientUser.id || 
                      targetPO.customerEmail === clientUser.email ||
                      brandMatches ||
                      targetPO.clientId === 'cli_default' ||
                      (clientUser.brand_name === 'Cielmora' && targetPO.clientId === 'cli_default');
      
      if (isOwner) {
        setOrder(targetPO);
      } else {
        onBack?.();
      }
    }
  }, [id, clientPOs, trashPOs, loadingPOs, clientUser]);

  const handleSimulatePayment = async () => {
    if (!order) return;
    
    const nowStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) + ' ' + new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    
    const updates = {
      statusProduksi: 'SEDANG PO ITEM',
      statusBayar: order.dpPercentage === 100 ? 'LUNAS' : `DP ${order.dpPercentage || 50}%`,
      timeline: (order.timeline || []).map((step, idx) => {
        if (idx === 0) return { ...step, active: false };
        if (idx === 1) return { ...step, status: 'Dikonfirmasi Tim Satala', done: true, date: nowStr, desc: `Konfirmasi Tim Satala Berhasil (${order.dpPercentage === 100 ? 'Lunas 100%' : 'DP ' + (order.dpPercentage || 50) + '%'})` };
        if (idx === 2) return { ...step, status: 'Sedang PO Item', done: true, active: true, date: nowStr, desc: 'Pengadaan Bahan & Kemasan (PO Item)' };
        return step;
      })
    };
    
    await updatePO(order.id, updates);
    setOrder(prev => ({ ...prev, ...updates }));
  };

  if (!order) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-slate-400 font-bold">Memuat detail pesanan...</p>
      </div>
    );
  }

  const totalProductsPrice = (order?.items || []).reduce((sum, item) => sum + (item.total_harga_jual || item.hargaTotal || 0), 0);
  const totalLegalitasPrice = (order?.legalitas || []).reduce((sum, leg) => sum + (leg.harga_jual * (leg.qty_variant || 1)), 0);
  const totalPrice = totalProductsPrice + totalLegalitasPrice;
  const totalPembayaran = (totalPrice * ((order?.dpPercentage || 100) / 100)) + (order?.shippingCost || 0);
  const sisaPelunasan = totalPrice - (totalPrice * ((order?.dpPercentage || 100) / 100));

  // Real-time Production Progress calculation
  const matchedProdOrder = prodOrders.find(p => 
    p.order_number === order?.id || 
    p.id === order?.id || 
    (p.brand_name && order?.brand_name && p.brand_name.toLowerCase() === order.brand_name.toLowerCase() && (p.order_number?.includes(order.id) || order.id?.includes(p.order_number)))
  );

  const orderTasks = prodTasks.filter(t => 
    t.po_id === matchedProdOrder?.id || 
    t.po_id === order?.id || 
    t.order_number === order?.id || 
    (matchedProdOrder?.order_number && t.order_number === matchedProdOrder.order_number)
  );

  let progressPercent = 0;
  let stageLabel = 'Belum Dikonfirmasi';
  let isCompleted = false;

  if (orderTasks.length > 0) {
    const completedCount = orderTasks.filter(t => t.status === 'completed').length;
    progressPercent = Math.round((completedCount / orderTasks.length) * 100);
    const inProgTask = orderTasks.find(t => t.status === 'in_progress');
    
    if (progressPercent === 100 || matchedProdOrder?.status === 'selesai' || matchedProdOrder?.status === 'siap_kirim') {
      progressPercent = 100;
      stageLabel = 'Selesai Produksi & QC';
      isCompleted = true;
    } else if (inProgTask) {
      stageLabel = `Sedang: ${inProgTask.task_name || inProgTask.activity_type || 'Proses Produksi'}`;
    } else {
      stageLabel = `${completedCount}/${orderTasks.length} Tahap Selesai`;
    }
  } else {
    const sp = (order?.statusProduksi || '').toUpperCase();
    const sb = (order?.statusBayar || '').toUpperCase();
    const prodStatus = (matchedProdOrder?.status || '').toLowerCase();

    if (sp.includes('SELESAI') || sp.includes('SIAP') || sp.includes('SAMPAI') || prodStatus === 'selesai' || prodStatus === 'siap_kirim') {
      progressPercent = 100;
      stageLabel = 'Selesai & Siap Kirim';
      isCompleted = true;
    } else if (sp.includes('KIRIM')) {
      progressPercent = 100;
      stageLabel = 'Pesanan Telah Dikirim';
      isCompleted = true;
    } else if (sp.includes('PROSES') || sp.includes('PROGRESS') || prodStatus === 'in_progress' || prodStatus === 'diracik' || prodStatus === 'maserasi' || prodStatus === 'filling' || prodStatus === 'packing') {
      progressPercent = 65;
      stageLabel = 'Sedang Diproduksi di Pabrik';
    } else if (
      prodStatus === 'antrian_produksi' || 
      sp === 'ANTREAN PRODUKSI' || 
      sp === 'ANTRIAN PRODUKSI' || 
      order?.items_ready === true || 
      order?.all_items_ready === true
    ) {
      progressPercent = 35;
      stageLabel = 'Antrean Produksi';
    } else if (
      prodStatus === 'sedang_po_item' || 
      sp === 'SEDANG PO ITEM' || 
      sp.includes('MENUNGGU PRODUKSI') || 
      (sb !== 'BELUM DIBAYAR' && sb !== 'BELUM DIKONFIRMASI' && !sb.includes('MENUNGGU'))
    ) {
      progressPercent = 25;
      stageLabel = 'Sedang PO Item';
    } else {
      progressPercent = 10;
      stageLabel = 'Menunggu Konfirmasi Tim Satala';
    }
  }

  const orderStatusUpper = (order?.statusProduksi || '').toUpperCase();
  const isCompletedFinal = isCompleted || 
    progressPercent === 100 || 
    orderStatusUpper.includes('SELESAI') || 
    orderStatusUpper.includes('SAMPAI') || 
    orderStatusUpper.includes('SIAP');

  const sjPayload = order ? {
    id: order.id,
    order_number: order.id,
    brand_name: order.brand_name || order.customerName || clientUser?.brand_name || 'Mitra Satala',
    customerName: order.customerName || clientUser?.company_name || 'Klien Satala',
    customerAddress: order.customerAddress || clientUser?.address || 'Alamat Penerima',
    phone: order.phone || clientUser?.phone || '-',
    courier: order.courier || 'Kurir Ekspedisi',
    resi: order.resi || '-',
    shipped_at: order.date || order.created_date,
    products: (order.products || order.items || []).map(p => ({
      product_name: p.nama_product || p.nama || 'Produk Maklon',
      variant_name: p.nama_product || p.nama || 'Varian Produk',
      botol_ukuran_label: p.selected_size || p.botol_ukuran_client || 30,
      qty: p.qty || 1,
      qty_akhir: p.qty || 1,
      qty_dikirim: p.qty || 1
    })),
    items: (order.items || []).map(p => ({
      variant_name: p.nama_product || p.nama || 'Varian Produk',
      qty_akhir: p.qty || 1,
      qty_dikirim: p.qty || 1,
      catatan: ''
    })),
    ...(matchedProdOrder || {})
  } : null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-5xl mx-auto pb-20 print:p-0 print:max-w-none print:bg-white">
      
      {/* ─── SCREEN DASHBOARD LAYOUT (Sembunyikan saat cetak) ─── */}
      <div className="print:hidden">
        {/* Header Modal / Halaman */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 border-b border-[#E6D5B8] pb-6">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-[#F9F5EC] rounded-full text-[#A39171] transition-colors cursor-pointer">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-[#302621]">Detail Pesanan</h1>
                <span className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-md border ${
                  isCompletedFinal ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 
                  stageLabel === 'Sedang PO Item' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  stageLabel === 'Antrean Produksi' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                  stageLabel.includes('Konfirmasi') ? 'bg-rose-50 text-rose-700 border-rose-200' :
                  'bg-blue-50 text-blue-700 border-blue-200'
                }`}>
                  {stageLabel.toUpperCase()}
                </span>
              </div>
              <p className="text-[#7A695B] font-mono text-xs mt-0.5">{order.id}</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Tombol Download Surat Jalan */}
            {isCompletedFinal ? (
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => setShowSuratJalanModal(true)} 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2 shadow-sm cursor-pointer"
                >
                  <Truck className="w-4 h-4" /> Download Surat Jalan
                </Button>
                <Link
                  to={`/surat-jalan/${encodeURIComponent(order.id)}`}
                  target="_blank"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-300 transition-colors shadow-xs"
                  title="Buka Surat Jalan Resmi Terverifikasi (Persis seperti hasil scan QR code pada label dus)"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-slate-500" /> Buka SJ Digital (QR)
                </Link>
              </div>
            ) : (
              <div 
                className="text-xs font-semibold text-slate-400 flex items-center gap-1.5 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 cursor-not-allowed"
                title="Surat Jalan resmi dapat diunduh setelah tahapan produksi selesai"
              >
                <Truck className="w-4 h-4 text-slate-400" />
                <span>Surat Jalan (Saat Selesai)</span>
              </div>
            )}

            <Button onClick={handlePrint} variant="outline" className="border-[#D4AF37] text-[#8C6208] hover:bg-[#F9F5EC] gap-2 cursor-pointer">
              <Download className="w-4 h-4" /> Download/Cetak Invoice
            </Button>
          </div>
        </div>

        {/* PESAN INVOICE TERHAPUS */}
        {order.deleted_at && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 shadow-sm mb-6 flex items-start gap-4">
            <div className="p-3 bg-rose-100 rounded-full text-rose-700 shrink-0">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-bold text-rose-800 text-lg">Invoice Dihapus</h4>
              <p className="text-sm text-rose-700 mt-1 leading-relaxed">
                Invoice ini sudah dihapus dari sistem pada <strong>{new Date(order.deleted_at).toLocaleString('id-ID')}</strong>
                {order.deleted_by && <span> oleh <strong>{order.deleted_by}</strong></span>}.
              </p>
            </div>
          </div>
        )}

        {/* SIMULASI PEMBAYARAN */}
        {!order.deleted_at && (order.statusBayar === 'BELUM DIBAYAR' || order.statusBayar === 'BELUM DIKONFIRMASI' || (order.statusBayar || '').includes('MENUNGGU')) && (
          <div className="bg-gradient-to-r from-red-50 to-amber-50 border border-amber-200 rounded-2xl p-6 shadow-sm mb-6 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex gap-4 items-start">
              <div className="p-3 bg-amber-100 rounded-full text-amber-700 shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-bold text-[#302621] text-lg">Menunggu Konfirmasi Tim Satala</h4>
                <p className="text-sm text-[#7A695B] mt-1 leading-relaxed">
                  {order.paymentMethod === 'qris' ? (
                    <span>Silakan scan kode QRIS pembayaran Anda sebesar <strong className="text-[#8C6208]">Rp {fmt(totalPembayaran)}</strong>.</span>
                  ) : (
                    <span>Silakan transfer sebesar <strong className="text-[#8C6208]">Rp {fmt(totalPembayaran)}</strong> ke rekening <strong>{accountsMap[order.paymentMethod]?.name || order.paymentMethod?.toUpperCase() || 'BCA'} {accountsMap[order.paymentMethod]?.number || ''}</strong>.</span>
                  )}
                </p>
              </div>
            </div>
            <div className="shrink-0 flex items-center gap-4">
              {order.paymentMethod === 'qris' && (
                <div className="bg-white p-2 border border-slate-200 rounded-lg shadow-sm">
                  <QrCode className="w-16 h-16 text-slate-700" />
                </div>
              )}
              <Button onClick={handleSimulatePayment} className="bg-[#A1C038] hover:bg-[#8CA831] text-white font-bold h-12 px-6 rounded-xl flex items-center gap-2 shadow-sm">
                Konfirmasi Tim Satala (Simulasi)
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* REAL-TIME PRODUCTION PROGRESS BANNER */}
          <div className="bg-gradient-to-r from-[#FFFDF9] to-white border border-[#E6D5B8] rounded-2xl p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#F9F5EC] border border-[#E6D5B8] flex items-center justify-center text-[#8C6208]">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-[10px] font-bold text-[#8C6208] uppercase tracking-wider block">Status Produksi Pabrik Real-Time</span>
                  <h3 className="font-black text-[#302621] text-base">{stageLabel}</h3>
                </div>
              </div>
              <div className="text-left sm:text-right">
                <span className="text-2xl font-black text-[#8C6208]">{progressPercent}%</span>
                <span className="text-xs font-semibold text-slate-400 block">Selesai Produksi</span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-[#F9F5EC] border border-[#E6D5B8]/50 rounded-full h-3 overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 rounded-full ${
                  progressPercent === 100 
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600' 
                    : 'bg-gradient-to-r from-[#D4AF37] to-[#8C6208]'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            {orderTasks.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#E6D5B8]/60 flex items-center justify-between text-xs text-[#7A695B]">
                <span>
                  {orderTasks.filter(t => t.status === 'completed').length} dari {orderTasks.length} tahapan pabrik telah terselesaikan
                </span>
                <button 
                  type="button" 
                  onClick={() => setShowTaskDetailModal(true)}
                  className="text-[#8C6208] font-bold hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <Layers className="w-3.5 h-3.5" /> Lihat Rincian Tahapan Pabrik
                </button>
              </div>
            )}
          </div>

          {/* PROGRESS TRACKER */}
          <div className="bg-white border border-[#E6D5B8] rounded-2xl p-6 shadow-sm">
            <div className="flex justify-between items-center relative">
              {/* Garis background */}
              <div className="absolute left-6 right-6 top-5 h-1 bg-slate-100 -z-10"></div>
              
              {order.timeline.map((step, idx) => (
                <div key={idx} className="flex flex-col items-center flex-1 z-10 bg-white px-2 text-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${
                    step.active ? 'bg-[#D4AF37] text-white ring-4 ring-[#F9F5EC]' : 
                    step.done ? 'bg-[#8C6208] text-white' : 'bg-slate-100 text-slate-300'
                  }`}>
                    {idx === 0 && <Package className="w-5 h-5" />}
                    {idx === 1 && <CreditCard className="w-5 h-5" />}
                    {idx === 2 && <Clock className="w-5 h-5" />}
                    {idx === 3 && <Package className="w-5 h-5" />}
                    {idx === 4 && <Truck className="w-5 h-5" />}
                    {idx === 5 && <CheckCircle className="w-5 h-5" />}
                  </div>
                  <p className={`text-xs font-bold ${step.done ? 'text-[#302621]' : 'text-slate-400'}`}>{step.status}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{step.date}</p>
                </div>
              ))}
            </div>
          </div>

          {/* DETAIL PRODUK */}
          <div className="bg-white border border-[#E6D5B8] rounded-2xl p-6 shadow-sm">
            <h3 className="font-bold text-[#302621] mb-4 text-lg border-b border-slate-100 pb-3">Detail Produk</h3>
            <div className="space-y-4">
              {order.items.map((item, idx) => {
                // Cari nama produk di database bahan
                const dbBahan = databaseBahanList.find(b => b.nama_varian === (item.nama_product || item.nama));
                const imageUrl = dbBahan?.image_url;

                return (
                  <div key={idx} className="flex flex-col gap-4 p-4 border border-slate-100 rounded-xl">
                    <div className="flex gap-6 items-center">
                      <div className="w-16 h-16 shrink-0 bg-slate-50 border border-slate-200 rounded-lg flex flex-col items-center justify-center text-slate-400">
                        <Package className="w-6 h-6 mb-1" />
                        <span className="text-[9px] font-bold">Produk</span>
                      </div>

                      <div className="flex-1">
                        <h4 className="font-bold text-[#302621] text-lg">{item.nama_product || item.nama}</h4>
                        <p className="text-sm text-[#7A695B]">Kuantitas: <span className="font-semibold">{item.qty} pcs</span></p>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-black text-xl text-[#302621]">Rp {fmt(item.hargaTotal)}</p>
                      </div>
                    </div>
                    
                    <div className="mt-2 border-t border-slate-100 pt-4">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Desain Artwork & Referensi Visual</p>
                      <ClickToPreviewImage src={imageUrl} alt={`Artwork ${item.nama_product || item.nama}`} />
                    </div>
                  </div>
                );
              })}

              {(order.legalitas || []).map((leg, idx) => (
                <div key={`leg-${idx}`} className="flex gap-6 items-center p-4 border border-blue-50 bg-blue-50/20 rounded-xl">
                  
                  <div className="w-24 h-24 shrink-0 bg-blue-50 border border-blue-100 rounded-lg flex flex-col items-center justify-center text-blue-400">
                    <ShieldCheck className="w-8 h-8 mb-2" />
                    <span className="text-[10px] font-bold uppercase">Legalitas</span>
                  </div>

                  <div className="flex-1">
                    <h4 className="font-bold text-blue-900 text-lg">{leg.nama}</h4>
                    <p className="text-sm text-blue-700">Layanan Jasa Legalitas & Sertifikasi (Qty: {leg.qty_variant || 1})</p>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-black text-xl text-blue-900">Rp {fmt(leg.harga_jual * (leg.qty_variant || 1))}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* PENGIRIMAN & PEMBAYARAN GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* INFO PENGIRIMAN */}
            <div className="bg-white border border-[#E6D5B8] rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="font-bold text-[#302621] text-lg">Informasi Pengiriman</h3>
                <span className="bg-[#302621] text-white text-[10px] px-2 py-0.5 rounded font-bold uppercase">{order.courier === 'Ambil Sendiri' ? 'Ambil Pabrik' : 'Diantar Kurir'}</span>
              </div>
              
              <div className="space-y-4 text-sm mb-6">
                <div className="flex">
                  <span className="w-32 text-[#7A695B]">Kurir</span>
                  <span className="font-semibold text-[#302621]">{order.courier}</span>
                </div>
                <div className="flex">
                  <span className="w-32 text-[#7A695B]">Nomor Resi</span>
                  <span className="font-bold text-[#8C6208]">{order.resi}</span>
                </div>
                <div className="flex">
                  <span className="w-32 text-[#7A695B]">Penerima</span>
                  <div>
                    <span className="font-semibold text-[#302621] block">{order.customerName}</span>
                    <span className="text-[#7A695B] text-xs leading-relaxed block mt-1">{order.customerAddress}</span>
                    <span className="text-[#7A695B] text-xs font-semibold">{order.phone}</span>
                  </div>
                </div>
              </div>

              {/* TIMELINE RESI */}
              {order.resiTimeline && order.resiTimeline.length > 0 && (
                <div className="border-t border-slate-100 pt-6">
                  <h4 className="font-bold text-sm text-[#302621] mb-4">Lacak Pengiriman</h4>
                  <div className="space-y-4 relative before:absolute before:inset-0 before:ml-2.5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
                    {order.resiTimeline.map((t, idx) => (
                      <div key={idx} className="relative flex items-start gap-4">
                        <div className="w-5 h-5 rounded-full bg-white border-2 border-[#D4AF37] flex items-center justify-center shrink-0 mt-0.5 z-10">
                          <div className="w-2 h-2 rounded-full bg-[#D4AF37]"></div>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 font-semibold">{t.time}</p>
                          <p className="text-sm font-medium text-[#302621]">{t.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RINCIAN PEMBAYARAN */}
            <div className="bg-white border border-[#E6D5B8] rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-[#302621] text-lg mb-6">Rincian Pembayaran</h3>
              
              <div className="space-y-3 text-sm border-b border-slate-100 pb-4 mb-4">
                <div className="flex justify-between text-[#7A695B]">
                  <span>Total Harga ({order.items.length} produk)</span>
                  <span className="font-semibold text-[#302621]">Rp {fmt(totalPrice)}</span>
                </div>
                <div className="flex justify-between text-[#7A695B]">
                  <span>{order.shippingCostLabel || 'Total Ongkos Kirim'}</span>
                  <span className="font-semibold text-[#302621]">Rp {fmt(order.shippingCost)}</span>
                </div>
                <div className="flex justify-between text-[#7A695B]">
                  <span>Skema Tagihan</span>
                  <span className="font-semibold text-[#302621]">{order.dpPercentage === 100 ? 'Lunas 100%' : `DP ${order.dpPercentage}%`}</span>
                </div>
              </div>

              <div className="bg-[#F9F5EC] border border-[#E6D5B8] p-4 rounded-xl">
                <p className="text-xs font-bold text-[#A39171] uppercase tracking-wider mb-1">
                  {order.statusBayar === 'BELUM DIBAYAR' ? 'Tagihan Down Payment (Belum Lunas)' : `Total Terbayar (DP ${order.dpPercentage}% + Ongkir)`}
                </p>
                <p className="text-3xl font-black text-[#8C6208]">Rp {fmt(totalPembayaran)}</p>
                
                <div className="mt-3 pt-3 border-t border-[#E6D5B8]/50 flex justify-between items-center text-xs">
                  <span className="text-[#7A695B]">Sisa Pelunasan</span>
                  <span className="font-bold text-[#302621]">Rp {fmt(sisaPelunasan)}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      
      {/* ─── HI-FI PRINT ONLY INVOICE (Hanya muncul saat dicetak/PDF) ─── */}
      <div className="hidden print:block font-sans text-black p-0 text-[12px] leading-relaxed w-full">
        <style type="text/css">{`
          @media print {
            @page { size: A4 portrait; margin: 10mm; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: white !important; }
          }
        `}</style>
        
        <div id="invoice-print-area" className="bg-white p-0 max-w-none w-full mx-auto border-none shadow-none">
          
          {/* Company Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex gap-4 items-center">
              {companySettings.logo_url ? (
                <img src={companySettings.logo_url} alt="Logo" className="w-14 h-14 object-contain" />
              ) : (
                <div className="w-14 h-14 bg-[#8C6208] rounded-full flex items-center justify-center shrink-0">
                  <span className="text-white text-3xl font-bold tracking-tighter">S</span>
                </div>
              )}
              <div>
                <h2 className="text-base font-black uppercase text-[#302621] tracking-tight">{companySettings.nama_perusahaan || 'PT. Satala Dermatech Essential'}</h2>
                <p className="text-[9px] text-slate-500 max-w-[420px]">
                  {companySettings.alamat_perusahaan || 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <h1 className="text-xl font-black text-slate-800 tracking-wider">INVOICE</h1>
              <span className="inline-block mt-1 border border-green-300 text-green-700 font-bold px-2 py-0.5 rounded text-[10px]">
                {order.dpPercentage === 100 ? 'LUNAS' : `DP ${order.dpPercentage}%`}
              </span>
            </div>
          </div>

          {/* Divider line in Gold */}
          <div className="border-b-[3px] border-[#D4AF37] mb-4"></div>

          {/* Meta Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="space-y-1 text-xs">
              <div className="flex">
                <span className="w-24 text-slate-500">Nomor</span>
                <span className="w-2">:</span>
                <span className="font-bold text-slate-800">{order.id}</span>
              </div>
              <div className="flex">
                <span className="w-24 text-slate-500">Tanggal</span>
                <span className="w-2">:</span>
                <span className="text-slate-800">{formatTanggalIndo(order.date)}</span>
              </div>
              {order.tanggal_jatuh_tempo && (
                <div className="flex">
                  <span className="w-24 text-slate-500">Jatuh Tempo</span>
                  <span className="w-2">:</span>
                  <span className="text-rose-600 font-bold">{formatTanggalIndo(order.tanggal_jatuh_tempo)}</span>
                </div>
              )}
              <div className="flex">
                <span className="w-24 text-slate-500">Payment Term</span>
                <span className="w-2">:</span>
                <span className="font-bold text-slate-800 uppercase">
                  {order.paymentTerms === 'tempo' ? `TEMPO (${order.tempoDays || 30} HARI)` : 'CASH (DP 50% + 50%)'}
                </span>
              </div>
              {order.bank_tujuan && (
                <div className="flex">
                  <span className="w-24 text-slate-500">Pembayaran Ke</span>
                  <span className="w-2">:</span>
                  <span className="font-medium text-slate-800 uppercase">BANK {order.bank_tujuan}</span>
                </div>
              )}
            </div>

            <div className="border border-slate-200 bg-slate-50/50 p-2.5 rounded-lg text-xs">
              <p className="font-bold text-slate-800 mb-0.5">Kepada:</p>
              <p className="font-bold text-[#8C6208] text-xs">{order.customerName}</p>
              <p className="text-[9px] text-slate-500 mt-1 leading-tight">
                Pembelian produk OEM / Maklon Satala Dermatech
              </p>
              <p className="text-[9px] text-slate-500">Di Tempat.</p>
            </div>
          </div>

          <div className="mb-4 text-xs">
            <p className="font-bold text-slate-700">Dengan hormat,</p>
            <p className="text-slate-500 text-[10px] mt-0.5">
              Berdasarkan kesepakatan bersama, dengan ini kami mengajukan invoice tersebut sebagai berikut :
            </p>
          </div>

          <table className="w-full text-left border-collapse mb-4 text-[10px]">
            <thead>
              <tr className="bg-[#302621] text-[#D4AF37]">
                <th className="py-2 px-3 font-bold w-8 text-center rounded-tl-md">No</th>
                <th className="py-2 px-3 font-bold">Nama Produk / Layanan</th>
                <th className="py-2 px-3 font-bold">Keterangan</th>
                <th className="py-2 px-3 font-bold text-center w-12">Qty</th>
                <th className="py-2 px-3 font-bold text-right w-28">Harga/Pcs</th>
                <th className="py-2 px-3 font-bold text-right w-28 rounded-tr-md">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, idx) => {
                const qty = item.qty || 1;
                const subtotal = item.hargaTotal || 0;
                const price = item.harga_jual_per_pcs || (subtotal / qty) || 0;
                return (
                  <tr key={`p-${idx}`} className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
                    <td className="py-1 px-2 text-center text-slate-500">{idx + 1}</td>
                    <td className="py-1 px-2 font-semibold text-slate-800">{item.nama_product || item.nama}</td>
                    <td className="py-1 px-2 text-slate-500 text-[9px]">
                      {(() => {
                        const vol = item.botol_ukuran_aktual || item.botol_ukuran_label || item.botol_ukuran_client || item.ukuran_botol_ml || item.volume_ml || item.ukuran || item.ukuran_ml;
                        const volText = vol ? vol : '-';
                        return `Volume ${volText}` + (String(volText).toLowerCase().includes('ml') ? '' : ' ml');
                      })()}
                    </td>
                    <td className="py-1 px-2 text-center font-medium">{qty}</td>
                    <td className="py-1 px-2 text-right">Rp {fmt(price)}</td>
                    <td className="py-1 px-2 text-right font-semibold text-slate-800">Rp {fmt(subtotal)}</td>
                  </tr>
                );
              })}

              {(() => {
                const flattenedLegalitas = [];
                (order.legalitas || []).forEach((leg) => {
                  const hargaJual = Number(leg.harga_jual !== undefined && leg.harga_jual !== '' ? leg.harga_jual : (leg.nominal || 0));
                  const qty = leg.qty_variant || (Array.isArray(leg.selected_variants) && leg.selected_variants.length > 0 ? leg.selected_variants.length : 1);
                  flattenedLegalitas.push({
                    nama: leg.nama,
                    keterangan: 'Layanan Legalitas Maklon',
                    qty: qty,
                    harga: hargaJual,
                    subtotal: hargaJual * qty,
                  });
                });

                const startIdx = (order.items || []).length;
                return flattenedLegalitas.map((item, idx) => (
                  <tr key={`l-${idx}`} className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
                    <td className="py-1 px-2 text-center text-slate-500">{startIdx + idx + 1}</td>
                    <td className="py-1 px-2 font-semibold text-slate-800">{item.nama}</td>
                    <td className="py-1 px-2 text-slate-500 text-[9px]">{item.keterangan}</td>
                    <td className="py-1 px-2 text-center font-medium">{item.qty}</td>
                    <td className="py-1 px-2 text-right">Rp {fmt(item.harga)}</td>
                    <td className="py-1 px-2 text-right font-semibold text-slate-800">Rp {fmt(item.subtotal)}</td>
                  </tr>
                ));
              })()}

              {order.shippingCost > 0 && (
                <tr className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
                  <td className="py-1 px-2 text-center text-slate-500">
                    {(order.items || []).length + (order.legalitas || []).length + 1}
                  </td>
                  <td className="py-1 px-2 font-semibold text-slate-800">{order.shippingCostLabel || 'Biaya Pengiriman'}</td>
                  <td className="py-1 px-2 text-slate-500 text-[9px]">Biaya Tambahan / Ekspedisi</td>
                  <td className="py-1 px-2 text-center font-medium">1</td>
                  <td className="py-1 px-2 text-right">Rp {fmt(order.shippingCost)}</td>
                  <td className="py-1 px-2 text-right font-semibold text-slate-800">Rp {fmt(order.shippingCost)}</td>
                </tr>
              )}
              
              <tr className="bg-[#F9F5EC]">
                <td colSpan="4" className="py-2 px-3"></td>
                <td className="py-2 px-3 font-bold text-slate-700 text-right">Total Harga</td>
                <td className="py-2 px-3 font-black text-[#8C6208] text-right">Rp {fmt(totalPrice + order.shippingCost)}</td>
              </tr>

              {order.dpPercentage < 100 && (
                <>
                  <tr className="bg-emerald-50/60 border-t border-slate-100">
                    <td colSpan="4" className="py-2 px-3"></td>
                    <td className="py-2 px-3 font-bold text-emerald-700 text-right">Total Terbayar</td>
                    <td className="py-2 px-3 font-bold text-emerald-700 text-right">Rp {fmt((totalPrice + order.shippingCost) * (order.dpPercentage / 100))}</td>
                  </tr>
                  <tr className="bg-amber-50/60">
                    <td colSpan="4" className="py-2 px-3"></td>
                    <td className="py-2 px-3 font-bold text-amber-700 text-right">Sisa Tagihan</td>
                    <td className="py-2 px-3 font-bold text-amber-700 text-right">Rp {fmt(sisaPelunasan)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          <div className="mb-6 bg-slate-50/50 p-2.5 rounded-lg border border-slate-100 text-xs">
            <p className="font-bold text-slate-700">Terbilang:</p>
            <p className="text-[10px] italic text-[#8C6208] font-medium mt-0.5">
              {terbilang(order.statusBayar === 'BELUM DIBAYAR' ? totalPembayaran : totalPrice + order.shippingCost)}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 items-start mt-6 text-xs">
            <div>
              <p className="font-bold text-slate-700 mb-1">Noted:</p>
              <p className="text-slate-500 text-[10px] mb-3">Pembayaran Harap Mencantumkan Detail Invoice</p>

              <div className="bg-slate-50 border border-slate-100 p-2.5 rounded-lg space-y-1 text-[9px] max-w-[280px]">
                <div className="flex">
                  <span className="w-20 text-slate-500">Account</span>
                  <span className="font-bold text-slate-700">: {order.paymentMethod === 'qris' ? (companySettings.nama_perusahaan || 'PT. Satala Dermatech Essential') : (accountsMap[order.paymentMethod]?.name || companySettings.nama_perusahaan || 'APA SAJA BISA CV')}</span>
                </div>
                <div className="flex">
                  <span className="w-20 text-slate-500">Bank</span>
                  <span className="font-bold text-slate-700">: {order.paymentMethod === 'qris' ? 'QRIS' : (order.paymentMethod?.toUpperCase() || 'BCA')}</span>
                </div>
                <div className="flex">
                  <span className="w-20 text-slate-500">No Rekening</span>
                  <span className="font-bold text-slate-700">: {order.paymentMethod === 'qris' ? 'QRIS / SCAN QR CODE' : (accountsMap[order.paymentMethod]?.number || '1741359516')}</span>
                </div>
              </div>
            </div>

            <div className="text-right flex flex-col items-end pr-4">
              <p className="text-[10px] text-slate-500 mb-1">Bogor, {formatTanggalIndo(order.date)}</p>
              
              <div className="relative w-48 h-20 my-1 flex justify-center items-center">
                {companySettings.stempel_url ? (
                  <img src={companySettings.stempel_url} alt="Stempel" className="absolute left-6 -top-1 pointer-events-none w-20 h-20 object-contain opacity-80" />
                ) : (
                  <div className="absolute left-6 -top-1 pointer-events-none scale-[0.8]">
                    <svg width="90" height="90" viewBox="0 0 100 100" className="opacity-80">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#D4AF37" strokeWidth="2" strokeDasharray="3 2" />
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#D4AF37" strokeWidth="1" />
                      <path id="circlePath3" d="M 15 50 A 35 35 0 0 1 85 50" fill="none" stroke="none" />
                      <text fill="#D4AF37" fontSize="6" fontWeight="bold" letterSpacing="0.4">
                        <textPath href="#circlePath3" startOffset="50%" textAnchor="middle">
                          PT. SATALA DERMATECH ESSENTIAL
                        </textPath>
                      </text>
                      <path id="circlePathBottom3" d="M 85 50 A 35 35 0 0 1 15 50" fill="none" stroke="none" />
                      <text fill="#D4AF37" fontSize="6.5" fontWeight="bold" letterSpacing="0.4">
                        <textPath href="#circlePathBottom3" startOffset="50%" textAnchor="middle">
                          ★ BOGOR ★
                        </textPath>
                      </text>
                      <path d="M 45 35 C 45 30, 55 30, 55 35 C 55 40, 45 42, 45 48 C 45 53, 55 53, 55 48" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                )}

                {companySettings.ttd_url ? (
                  <img src={companySettings.ttd_url} alt="Tanda Tangan" className="w-24 h-16 object-contain relative z-10" />
                ) : (
                  <span className="font-serif italic text-2xl text-blue-900/60 font-semibold tracking-wider relative z-10 rotate-[-4deg]" style={{ fontFamily: "'Brush Script MT', cursive" }}>
                    {companySettings.nama_direktur || 'Umar Syarif'}
                  </span>
                )}
              </div>

              <div className="text-center w-48 border-t border-slate-200 pt-1">
                <p className="font-bold text-slate-800 text-[10px]">{companySettings.nama_direktur || 'Umar Syarif'}</p>
                <p className="text-slate-400 text-[8px] uppercase tracking-wider mt-0.5">Direktur</p>
              </div>
            </div>
          </div>

          <div className="border-b-[2px] border-[#D4AF37] mt-8"></div>
        </div>
      </div>

      {/* MODAL SURAT JALAN PRINT / DOWNLOAD */}
      {showSuratJalanModal && sjPayload && (
        <SuratJalanPrint
          order={sjPayload}
          companySettings={companySettings}
          onClose={() => setShowSuratJalanModal(false)}
        />
      )}

      {/* MODAL CHECKLIST TAHAPAN PABRIK */}
      {showTaskDetailModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh] border border-[#E6D5B8]">
            <div className="px-6 py-4 border-b border-[#E6D5B8] bg-[#F9F5EC] flex justify-between items-center">
              <div>
                <span className="text-[10px] font-bold text-[#8C6208] uppercase tracking-wider block">Tahapan Produksi Pabrik</span>
                <h3 className="font-black text-base text-[#302621]">{order.id}</h3>
              </div>
              <button 
                onClick={() => setShowTaskDetailModal(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-3 text-xs">
              <div className="bg-[#FFFDF9] p-4 rounded-2xl border border-[#E6D5B8] mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-[#302621]">Status Keseluruhan:</span>
                  <span className="font-black text-[#8C6208]">{progressPercent}%</span>
                </div>
                <p className="text-[#7A695B] text-[11px]">{stageLabel}</p>
              </div>

              <p className="font-bold text-[#302621] uppercase text-[10px] tracking-wider mb-2">Checklist Aktivitas:</p>
              
              <div className="divide-y divide-gray-100 border border-[#E6D5B8]/60 rounded-2xl overflow-hidden bg-white">
                {orderTasks.map((t, idx) => {
                  const isDone = t.status === 'completed';
                  const isInProg = t.status === 'in_progress';

                  return (
                    <div key={idx} className="p-3.5 flex items-center justify-between hover:bg-[#FFFDF9]">
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                          isDone 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : isInProg 
                            ? 'bg-blue-100 text-blue-700 animate-pulse' 
                            : 'bg-slate-100 text-slate-400'
                        }`}>
                          {isDone ? '✓' : idx + 1}
                        </div>
                        <div>
                          <p className="font-bold text-[#302621]">{t.task_name || t.activity_type}</p>
                          <p className="text-[10px] text-[#7A695B] capitalize">
                            {t.product_name || 'Global Aktivitas'} • {t.activity_type || 'Produksi'}
                          </p>
                        </div>
                      </div>

                      <div>
                        {isDone ? (
                          <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                            Selesai
                          </span>
                        ) : isInProg ? (
                          <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-200">
                            Sedang Dikerjakan
                          </span>
                        ) : (
                          <span className="bg-slate-50 text-slate-500 text-[10px] font-medium px-2 py-0.5 rounded-full">
                            Dalam Antrean
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="px-6 py-4 bg-[#F9F5EC] border-t border-[#E6D5B8] text-right">
              <button 
                onClick={() => setShowTaskDetailModal(false)}
                className="text-xs font-bold px-5 py-2 rounded-xl border border-[#E6D5B8] bg-white hover:bg-slate-50 text-[#302621] cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
